import {
  LabelsData,
  type DependencyMapNode,
  type DependencyMapReference,
  type DependencyMapReferenceParent,
} from '../types';
import { getIconColorFromScopeData } from '../utils';
import { calculateTextColors, nodeFromComponentId } from './utils';
import { classes, type ContextSort } from '@ardoq/common-helpers';
import { ArdoqId, GroupingRule, ReferenceDirection } from '@ardoq/api-types';
import { LI_BACKGROUND_CLASS_NAME } from '@ardoq/global-consts';
import { RawGraphEdge2 } from '@ardoq/graph';
import { getReferenceComparator } from '../getReferenceComparator';
import { groupBy } from 'lodash';
import type { Locale } from '@ardoq/locale';
import type { EnhancedScopeData } from '@ardoq/data-model';
import {
  componentUtils,
  referenceUtils,
  type FilterColorData,
} from '@ardoq/scope-data';

interface GetReferenceParentArgs {
  nodeId: string;
  componentId: ArdoqId;
  depth: number;
  maxDepth: number;
  nodeMap?: Map<string, DependencyMapNode>;
  labelsData: LabelsData;
  isNewConditionalFormatting?: boolean;
  scopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
  groupBys: GroupingRule[];
  includeOnlyParentsInScope: boolean;
}
export const referenceParent = ({
  nodeId,
  componentId,
  depth,
  maxDepth,
  nodeMap,
  labelsData,
  isNewConditionalFormatting,
  scopeData,
  filterColorData,
  groupBys,
  includeOnlyParentsInScope,
}: GetReferenceParentArgs): DependencyMapReferenceParent | null => {
  if (depth >= maxDepth) {
    return null;
  }

  const parentId = includeOnlyParentsInScope
    ? componentUtils.getParentIdIfInScope(scopeData, componentId, groupBys)
    : componentUtils.getParentId(scopeData, componentId);
  if (!parentId) {
    return null;
  }

  const degreeOfParenthood = depth + 1;
  const parentNodeId = `${nodeId}-parent`;
  const parentNode: DependencyMapNode = nodeMap?.get(parentId) ?? {
    ...nodeFromComponentId(
      parentId,
      labelsData,
      isNewConditionalFormatting,
      scopeData,
      filterColorData
    ),
    degreeOfParenthood,
    id: parentNodeId,
  };
  return {
    ...parentNode,
    degreeOfParenthood,
    children: null,
    incomingReferences: null,
    outgoingReferences: null,
    parent: referenceParent({
      nodeId: parentNodeId,
      componentId: parentId,
      depth: degreeOfParenthood,
      maxDepth,
      nodeMap,
      labelsData,
      isNewConditionalFormatting,
      scopeData,
      filterColorData,
      groupBys,
      includeOnlyParentsInScope,
    }),
  };
};

type RawReferenceWithTypeId = RawGraphEdge2 & {
  referenceTypeId: string | null;
};

interface MapReferencesArgs {
  key: 'sourceId' | 'targetId';
  nodeMap: Map<string, DependencyMapNode>;
  referenceList: RawReferenceWithTypeId[];
  degreesOfParenthood: number;
  referenceTypeNames: Map<string, string>;
  labelsData: LabelsData;
  isNewConditionalFormatting?: boolean;
  sort: ContextSort;
  locale: Locale;
  scopeData: EnhancedScopeData;
  workspaceIds: ArdoqId[];
  filterColorData: FilterColorData;
  groupBys: GroupingRule[];
  includeOnlyParentsInScope: boolean;
}
const toDependencyMapReferenceImpl =
  ({
    key,
    nodeMap,
    degreesOfParenthood,
    labelsData,
    isNewConditionalFormatting,
    scopeData,
    filterColorData,
    groupBys,
    includeOnlyParentsInScope,
  }: MapReferencesArgs) =>
  (reference: RawReferenceWithTypeId) => {
    const isOutgoing = key === 'sourceId';
    const nodeId = isOutgoing ? reference.targetId : reference.sourceId;
    const node = nodeMap.get(nodeId)!;
    const componentId = node.modelId;
    const referenceId = reference.id;
    const className = classes(
      reference.modelId
        ? referenceUtils.getReferenceCSSClassNames(
            scopeData,
            filterColorData,
            reference.modelId
          )
        : null,
      LI_BACKGROUND_CLASS_NAME
    );
    const { fill, stroke } = reference.modelId
      ? referenceUtils.getReferenceDisplayColorAsSVGAttributes(
          scopeData,
          filterColorData,
          reference.modelId,
          { useAsBackgroundStyle: true, isNewConditionalFormatting }
        )
      : {};
    const { textColor, textModerateColor } = calculateTextColors(
      fill,
      isNewConditionalFormatting
    );

    const { mainLabel, otherLabels } =
      labelsData.edgeLabelsData.get(referenceId) ?? {};

    const referenceModel: DependencyMapReference = {
      referenceIds: [referenceId],
      referenceTypeId: reference.referenceTypeId,
      node,
      label: mainLabel ?? '',
      otherLabels,
      className,
      fill,
      stroke,
      textColor,
      textModerateColor,
      iconColor: getIconColorFromScopeData(
        scopeData,
        filterColorData,
        componentId,
        reference.modelId ?? componentId,
        true,
        isNewConditionalFormatting
      ),
      parent: referenceParent({
        nodeId: referenceId,
        componentId,
        depth: 0,
        maxDepth: degreesOfParenthood,
        nodeMap,
        labelsData,
        isNewConditionalFormatting,
        scopeData,
        filterColorData,
        groupBys,
        includeOnlyParentsInScope,
      }),
      isCollapsedReference: false,
      sourceId: reference.sourceId,
      targetId: reference.targetId,
      direction: isOutgoing
        ? ReferenceDirection.OUTGOING
        : ReferenceDirection.INCOMING,
    };
    return referenceModel;
  };

const mapReferences = (args: MapReferencesArgs) => {
  const {
    key,
    referenceList,
    referenceTypeNames,
    sort,
    locale,
    scopeData,
    workspaceIds,
  } = args;
  const compareReferences = getReferenceComparator(
    referenceTypeNames,
    sort,
    locale,
    scopeData,
    workspaceIds
  );
  const toDependencyMapReference = toDependencyMapReferenceImpl(args);
  const dependencyMapReferences = referenceList.map(toDependencyMapReference);

  dependencyMapReferences.sort(compareReferences);

  return groupBy(dependencyMapReferences, key);
};

export { mapReferences };
